import { useState, useEffect, useMemo, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../auth/AuthContext';
import { getPerformanceReviewStatus } from '../api/performanceReviewApi';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import api from '../api/axios';
import { getUsers } from '../api/userApi';
import { IssueDetailModal } from '../pages/Issues';
import {
  isTerminalStatus,
  sortRowsForReview,
  sortBugsForReview,
  taskRowsOnly,
  bugRowsOnly,
  CLOSED_BUG_STATUSES,
  WorkItemCard,
  StatBox,
} from './profileWorkItems';
import PerformanceReviewManagerPanel from './PerformanceReviewManagerPanel';
import PerformanceReviewModal from './PerformanceReviewModal';
import { canSetupPerformanceReviewCycle } from './PerformanceReviewCycleSetup';
import CompensationHrSection from './CompensationHrSection';
import JoiningDateHrSection from './JoiningDateHrSection';
import MemberSprintOverview from './MemberSprintOverview';
import MemberPerformanceOverview from './MemberPerformanceOverview';
import { useMemberSprintStatus } from '../api/dashboardApi';
import { isAdminRole, normalizeDepartment } from '../constants/departments';

const MANAGER_EQUIV_ROLES = [
  'superadmin', 'admin', 'manager',
  'marketing_manager', 'brand_manager', 'pr_manager',
  'product_manager', 'lead', 'product', 'analyst', 'hr',
];

/** Profile roles treated as “people manager” — formal review open control is self-only from this modal. */
const PEOPLE_MANAGER_PROFILE_ROLES = new Set(['manager', 'product_manager']);

function toUserId(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const normalizeProjectKey = (r) => {
  const n = (r?.projectname || '').trim();
  return n ? n : '__none__';
};

const buildSprintOptions = (rows) => {
  const byKey = new Map();
  const multiProject = new Set((rows || []).map((r) => normalizeProjectKey(r))).size > 1;
  (rows || []).forEach((r) => {
    const sid = r.sprint_id != null && r.sprint_id !== '' ? String(r.sprint_id) : '';
    if (!sid) {
      if (!byKey.has('__backlog__')) byKey.set('__backlog__', { value: '__backlog__', label: 'Backlog' });
      return;
    }
    if (byKey.has(sid)) return;
    const sn = (r.sprint_name || '').trim() || `Sprint #${sid}`;
    const pn = (r.projectname || '').trim();
    const label = multiProject && pn ? `${sn} (${pn})` : sn;
    byKey.set(sid, { value: sid, label });
  });
  const opts = [...byKey.values()];
  opts.sort((a, b) => {
    if (a.value === '__backlog__') return -1;
    if (b.value === '__backlog__') return 1;
    return a.label.localeCompare(b.label);
  });
  return opts;
};

const selectFilterStyle = {
  fontSize: '0.82rem',
  fontWeight: 600,
  color: '#334155',
  padding: '0.45rem 0.65rem',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  minWidth: 140,
  cursor: 'pointer',
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'performance', label: 'Performance' },
  { id: 'commits', label: 'Code Activity' },
];

/** Fallback when `user.permissions` is empty — who may see compensation (masked until reveal). Edit is HR-only. */
function compensationViewByRole(viewerRole) {
  const r = String(viewerRole || '').toLowerCase();
  return ['superadmin', 'admin', 'manager', 'hr'].includes(r);
}

const EmployeeProfileModal = ({ userId, userName, onClose }) => {
  const { role: viewerRole, user: viewerUser } = useContext(AuthContext);
  const viewerPermissions = Array.isArray(viewerUser?.permissions) ? viewerUser.permissions : [];
  const resolvedViewerUserId = useMemo(
    () => toUserId(viewerUser?.id ?? viewerUser?.userId),
    [viewerUser]
  );
  const [activeTab, setActiveTab] = useState('overview');
  const [detailIssueId, setDetailIssueId] = useState(null);
  const queryClient = useQueryClient();
  const { data: prStatus } = useQuery({
    queryKey: ['performance-review-status'],
    queryFn: () => getPerformanceReviewStatus(),
    enabled: !!userId,
    staleTime: 30_000,
  });

  // Fetch employee profile data
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['employee-profile-modal', userId],
    queryFn: () => api.get(`/users/${userId}/profile`).then(r => r.data),
    enabled: !!userId,
  });

  const { data: sprintData, isLoading: sprintLoading } = useMemberSprintStatus(userId);

  const { data: assignUsers = [] } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => getUsers().then((r) => r.data || []),
    enabled: !!userId,
    staleTime: 60_000,
  });

  const handleIssueStatusChanged = () => {
    queryClient.invalidateQueries({ queryKey: ['employee-profile-modal', userId] });
    queryClient.invalidateQueries({ queryKey: ['employeeSprintStatus', userId] });
    queryClient.invalidateQueries({ queryKey: ['board'] });
    queryClient.invalidateQueries({ queryKey: ['sprints'] });
  };

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const currentWork = data?.current_work || [];
  const pastWork = data?.past_work || [];
  const commits = data?.commits || [];
  const user = data?.user || {};
  const stats = data?.stats || {};
  const projects = data?.projects || [];
  const sprintTimeline = data?.sprint_timeline || [];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 960,
          height: 'min(760px, calc(100vh - 2rem))',
          maxHeight: 'calc(100vh - 2rem)',
          minHeight: 420,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem 2rem',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>
              {user.full_name || userName || 'Employee Profile'}
            </h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              {user.email} • {user.role || 'Developer'}
              {user.team && ` • ${user.team}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#94a3b8',
              padding: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.25rem',
          padding: '0.75rem 2rem 0',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
        }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1.25rem',
                background: activeTab === tab.id ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #2C8FC7' : '2px solid transparent',
                color: activeTab === tab.id ? '#1272B3' : '#64748b',
                fontWeight: activeTab === tab.id ? 600 : 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                marginBottom: '-1px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content — minHeight 0 so flex child scrolls inside fixed-height dialog */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '1.5rem 2rem', position: 'relative' }}>
          {isFetching && !isLoading && (
            <LinearProgress
              sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, borderRadius: 0 }}
            />
          )}
          {isLoading ? (
            <div style={{
              padding: '3rem 2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              color: '#64748b',
            }}>
              <CircularProgress size={40} thickness={4} sx={{ color: '#2C8FC7' }} />
              <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>Loading profile…</div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Fetching workload and activity</div>
            </div>
          ) : (
            <>
              {/* Overview Tab — folds in Done + Completion % that used to live
                  on a separate Workload tab; both surfaced the same currentWork
                  rows so we keep one merged view. */}
              {activeTab === 'overview' && (
                <OverviewTab
                  currentWork={currentWork}
                  sprintData={sprintData}
                  sprintLoading={sprintLoading}
                  memberRole={user.role}
                  memberDepartment={user.department}
                  stats={stats}
                  onOpenIssue={(id) => setDetailIssueId(id)}
                />
              )}

              {/* Performance Tab */}
              {activeTab === 'performance' && (
                <PerformanceTab
                  stats={stats}
                  pastWork={pastWork}
                  projects={projects}
                  sprintTimeline={sprintTimeline}
                  memberUserId={userId}
                  memberDisplayName={user.full_name || user.email || userName || ''}
                  memberRole={user.role}
                  memberDepartment={user.department}
                  memberJoiningDate={user.joining_date || null}
                  viewerRole={viewerRole}
                  viewerDepartment={viewerUser?.department}
                  viewerUserId={resolvedViewerUserId}
                  viewerPermissions={viewerPermissions}
                  prStatus={prStatus}
                />
              )}
              
              {/* Code Activity Tab */}
              {activeTab === 'commits' && (
                <CodeActivityTab commits={commits} />
              )}
            </>
          )}
        </div>
      </div>
      {detailIssueId != null && (
        <IssueDetailModal
          issueId={String(detailIssueId)}
          users={assignUsers}
          onClose={() => setDetailIssueId(null)}
          onStatusChanged={handleIssueStatusChanged}
        />
      )}
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ currentWork, sprintData, sprintLoading, memberRole, memberDepartment, stats, onOpenIssue }) => {
  const [workSubTab, setWorkSubTab] = useState('tasks');
  const [filterProject, setFilterProject] = useState('all');
  const [filterSprint, setFilterSprint] = useState('all');

  useEffect(() => {
    setFilterSprint('all');
  }, [filterProject]);

  const projectOptions = useMemo(() => {
    const map = new Map();
    (currentWork || []).forEach((r) => {
      const key = normalizeProjectKey(r);
      const label = (r.projectname || '').trim() || '(No project)';
      if (!map.has(key)) map.set(key, label);
    });
    return [...map.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [currentWork]);

  const rowsForSprintOpts = useMemo(() => {
    if (filterProject === 'all') return currentWork || [];
    return (currentWork || []).filter((r) => normalizeProjectKey(r) === filterProject);
  }, [currentWork, filterProject]);

  const sprintOptions = useMemo(() => buildSprintOptions(rowsForSprintOpts), [rowsForSprintOpts]);

  const filteredWork = useMemo(() => {
    return (currentWork || []).filter((r) => {
      if (filterProject !== 'all' && normalizeProjectKey(r) !== filterProject) return false;
      if (filterSprint !== 'all') {
        const sid = r.sprint_id != null && r.sprint_id !== '' ? String(r.sprint_id) : '';
        if (filterSprint === '__backlog__') return !sid;
        return sid === filterSprint;
      }
      return true;
    });
  }, [currentWork, filterProject, filterSprint]);

  const tasks = sortRowsForReview(taskRowsOnly(filteredWork));
  const bugs = sortBugsForReview(bugRowsOnly(filteredWork));
  const openTasks = tasks.filter((t) => t.status === 'Open').length;
  const inProgressTasks = tasks.filter((t) => t.status === 'In Progress').length;
  const testingTasks = tasks.filter((t) => t.status === 'Testing').length;
  const doneTasks = tasks.filter((t) => isTerminalStatus(t.status)).length;
  const totalTasks = tasks.length;
  const completionPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const list = workSubTab === 'bugs' ? bugs : tasks;

  const bugOpen = bugs.filter((b) => b.status === 'Open').length;
  const bugBlockers = bugs.filter((b) => (b.priority || '') === 'Blocker').length;
  const bugFixed = bugs.filter((b) => b.status === 'Fixed' || b.status === 'Done').length;
  const bugClosed = bugs.filter(
    (b) => b.status === 'Closed' || CLOSED_BUG_STATUSES.has(b.status)
  ).length;
  const bugPipelineOther = bugs.filter((b) =>
    ['In Progress', 'Testing', 'Blocked'].includes(b.status)
  ).length;

  const showProjectFilter = projectOptions.length > 1;
  const showSprintFilter = sprintOptions.length > 1;

  return (
    <div>
      <MemberSprintOverview
        sprintData={sprintData}
        memberRole={memberRole}
        memberDepartment={memberDepartment}
        stats={stats}
        isLoading={sprintLoading}
      />

      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 0.75rem' }}>
        Work breakdown
      </h3>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {workSubTab === 'bugs' ? (
          <>
            <StatBox label="Open" value={bugOpen} color="#2C8FC7" />
            <StatBox label="Blocker" value={bugBlockers} color="#dc2626" />
            <StatBox label="Fixed" value={bugFixed} color="#22c55e" />
            <StatBox label="Closed" value={bugClosed} color="#64748b" />
          </>
        ) : (
          <>
            <StatBox label="Open" value={openTasks} color="#2C8FC7" />
            <StatBox label="In Progress" value={inProgressTasks} color="#8b5cf6" />
            <StatBox label="In Review" value={testingTasks} color="#f59e0b" />
            <StatBox label="Done" value={doneTasks} color="#16a34a" />
            <StatBox label="Completion %" value={`${completionPct}%`} color="#0f766e" />
          </>
        )}
      </div>
      {workSubTab === 'bugs' && bugPipelineOther > 0 && (
        <div style={{
          fontSize: '0.78rem',
          color: '#64748b',
          marginTop: '-1rem',
          marginBottom: '1.25rem',
          fontWeight: 600,
        }}>
          Also in workflow: {bugPipelineOther} in In Progress / In Review / Blocked
        </div>
      )}
      
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
              Current work
            </h3>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
              Active sprint items (incl. done) and open backlog on active projects
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', padding: 4, borderRadius: 10 }}>
            {[
              { id: 'tasks', label: `Tasks (${tasks.length})` },
              { id: 'bugs', label: `Bugs (${bugs.length})` },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setWorkSubTab(t.id)}
                style={{
                  padding: '0.45rem 0.9rem',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  background: workSubTab === t.id ? '#fff' : 'transparent',
                  color: workSubTab === t.id ? '#2C63AE' : '#64748b',
                  boxShadow: workSubTab === t.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {(showProjectFilter || showSprintFilter) && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem 1rem',
            alignItems: 'center',
            marginBottom: '1rem',
            padding: '0.65rem 0.85rem',
            background: '#f8fafc',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
          }}>
            {showProjectFilter && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
                Project
                <select
                  value={filterProject}
                  onChange={(e) => setFilterProject(e.target.value)}
                  style={selectFilterStyle}
                >
                  <option value="all">All projects</option>
                  {projectOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            )}
            {showSprintFilter && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
                Sprint
                <select
                  value={filterSprint}
                  onChange={(e) => setFilterSprint(e.target.value)}
                  style={selectFilterStyle}
                >
                  <option value="all">All sprints</option>
                  {sprintOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        {list.length === 0 ? (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: 8,
            color: '#94a3b8',
          }}>
            {workSubTab === 'bugs' ? 'No bugs match these filters' : 'No tasks match these filters'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            {list.map((task) => (
              <WorkItemCard key={task.issueid} task={task} onOpen={onOpenIssue} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Performance Tab Component
const PerformanceTab = ({
  stats,
  pastWork,
  projects,
  sprintTimeline,
  memberUserId,
  memberDisplayName,
  memberRole,
  memberDepartment,
  memberJoiningDate,
  viewerRole,
  viewerDepartment,
  viewerUserId,
  viewerPermissions,
  prStatus,
}) => {
  const prCycle = prStatus?.enabled && prStatus?.cycle ? prStatus.cycle : null;
  const showFormalReview =
    prStatus?.enabled &&
    prCycle &&
    MANAGER_EQUIV_ROLES.includes(String(viewerRole || '').toLowerCase());
  const memberIsPeopleManager = PEOPLE_MANAGER_PROFILE_ROLES.has(String(memberRole || '').toLowerCase());
  const vid = toUserId(viewerUserId);
  const mid = toUserId(memberUserId);
  const viewingOwnProfile = vid != null && mid != null && vid === mid;
  const viewerIsExec = ['superadmin', 'admin'].includes(String(viewerRole || '').toLowerCase());
  const [selfPrOpen, setSelfPrOpen] = useState(false);
  /** Own profile: self-review modal only (never manager calibration on self). */
  const showFormalReviewSelf = showFormalReview && viewingOwnProfile;
  /** Other people's profiles: manager panel (people-manager targets restricted unless exec viewer). */
  const showFormalReviewManagerPanel =
    showFormalReview && !viewingOwnProfile && (!memberIsPeopleManager || viewerIsExec);
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.info('[PerformanceTab] formal review panel', {
      memberUserId: mid,
      memberRole,
      memberIsPeopleManager,
      viewerUserId: vid,
      viewerRole,
      viewerIsExec,
      viewingOwnProfile,
      showFormalReview,
      showFormalReviewSelf,
      showFormalReviewManagerPanel,
    });
  }
  const perms = Array.isArray(viewerPermissions) ? viewerPermissions : [];
  const byPermView = perms.includes('hr.compensation.view') || perms.includes('hr.compensation.edit');
  const r = String(viewerRole || '').toLowerCase();
  const canViewCompensation = byPermView || compensationViewByRole(viewerRole);
  const canEditCompensation = r === 'hr';
  // Joining date: visible to anyone who can open this profile (non-confidential),
  // editable by HR only — same edit rule as compensation.
  const canEditJoiningDate = r === 'hr';
  const canManageKpis = MANAGER_EQUIV_ROLES.includes(r)
    && (isAdminRole(viewerRole) || normalizeDepartment(viewerDepartment) === normalizeDepartment(memberDepartment));

  return (
    <div>
      <MemberPerformanceOverview
        stats={stats}
        pastWork={pastWork}
        sprintTimeline={sprintTimeline}
        projects={projects}
        memberRole={memberRole}
        memberDepartment={memberDepartment}
        memberUserId={memberUserId}
        canManageKpis={canManageKpis}
      />

      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 1rem' }}>
        HR &amp; formal review
      </h3>
      {showFormalReviewSelf && prCycle ? (
        <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
            Your performance review ({prCycle.label || `${prCycle.year} Q${prCycle.quarter}`})
          </h4>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 0.85rem', lineHeight: 1.5 }}>
            Complete your self-rating and objectives here. Manager calibration is done by your manager after
            you submit; you will see that feedback once the review is published.
          </p>
          <button
            type="button"
            onClick={() => setSelfPrOpen(true)}
            style={{
              padding: '0.55rem 1rem',
              borderRadius: 8,
              border: 'none',
              background: '#1272B3',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            Open performance review
          </button>
          <PerformanceReviewModal
            open={selfPrOpen}
            onClose={() => setSelfPrOpen(false)}
            year={prCycle.year}
            quarter={prCycle.quarter}
            allowCycleSetup={canSetupPerformanceReviewCycle(viewerRole)}
          />
        </div>
      ) : null}

      {showFormalReviewManagerPanel ? (
        <PerformanceReviewManagerPanel
          employeeUserId={memberUserId}
          employeeName={memberDisplayName}
          year={prCycle.year}
          quarter={prCycle.quarter}
        />
      ) : null}

      {memberUserId ? (
        <JoiningDateHrSection
          employeeUserId={memberUserId}
          joiningDate={memberJoiningDate}
          canEdit={canEditJoiningDate}
        />
      ) : null}

      {canViewCompensation && memberUserId ? (
        <CompensationHrSection employeeUserId={memberUserId} canEdit={canEditCompensation} />
      ) : null}
    </div>
  );
};

// Code Activity Tab Component
const CodeActivityTab = ({ commits }) => {
  return (
    <div>
      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
        Recent Commits
        <span style={{ fontSize: '0.75rem', fontWeight: 600, background: '#f0fdf4', color: '#166534', padding: '2px 8px', borderRadius: 12 }}>
          {commits.length}
        </span>
      </h3>
      
      {commits.length === 0 ? (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: '#f8fafc',
          borderRadius: 8,
          color: '#94a3b8',
        }}>
          No recent commits found
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {commits.slice(0, 10).map((commit, idx) => (
            <div
              key={idx}
              style={{
                padding: '0.75rem',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', flex: 1 }}>
                  {commit.message || 'No message'}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', whiteSpace: 'nowrap', marginLeft: '1rem' }}>
                  {commit.date ? new Date(commit.date).toLocaleDateString() : ''}
                </div>
              </div>
              {commit.repository && (
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                  Repository: {commit.repository}
                </div>
              )}
              {commit.hash && (
                <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontFamily: 'monospace', marginTop: 4 }}>
                  {commit.hash.substring(0, 7)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmployeeProfileModal;
